import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { CssBaseline } from "@mui/material";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import SideMenu from "./pages/dashboard/components/SideMenu";
import AppNavbar from "./pages/dashboard/components/AppNavbar";
import AppTheme from "./pages//shared-theme/AppTheme";
import HomePage from "./pages/HomePage";
import PatrolsPage from "./pages/PatrolsPage";
import TasksPage from "./pages/TasksPage";
import CompletedTasksPage from "./pages/CompletedTasksPage";
import ErrorPage from "./pages/ErrorPage.js";
import Dashboard from "./pages/dashboard/Dashboard";
import { useDispatch, Provider, useSelector } from "react-redux";
import { fetchTasksFromAPI, fetchPatrolsFromAPI } from "./services/api";
//import { fetchConstants } from "./services/constantsAPI.js";
import { store } from "./redux/store"; // Import your Redux store
import MockLoginPage from './pages/MockLoginPage';
import PatrolUIPage from './pages/PatrolUIPage';
import Unauthorized from './pages/UnauthorizedPage.js';
import PrivateRoute from './auth/PrivateRoute';
import AppInitializer from './services/AppInitializer';
import NotificationSnackbar from "./components/widgets/NotificationSnackbar.js";
import { AuthProvider } from "react-oidc-context";
import CleanupUrl from "./utils/CleanupUrl.js";
import cognitoAuthConfig from "./utils/cognitoConfig.js";
//import { useReduxUserSync } from "./utils/hooks/useReduxUserSync.js";
import AuthGate from "./services/AuthGate.js";
import { ErrorBoundary } from "react-error-boundary";
import UpdateStorePage from "./pages/UpdateStorePage.js";
import { CALL_HANDLER, PATROL, CONTROL_TOWER } from "./utils/config.js";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "./index.css";

import {
  dataGridCustomizations,
  datePickersCustomizations,
} from "./pages/dashboard/theme/customizations";
import { setTokenUtils } from "./utils/httpClient.js";
import { AuthRedirectHandler } from "./utils/AuthRedirectHandler.js";
//import { useAutoLogout } from "./utils/hooks/useAutoLogout.js";

const xThemeComponents = {
  ...dataGridCustomizations,
  ...datePickersCustomizations,
};

import { LicenseInfo } from '@mui/x-license';
LicenseInfo.setLicenseKey('4bf043aee620ef1e146fec05dcae2e39Tz0xMTg2NTksRT0xNzg4NjUyNzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=');

const App = (props) => {
  const dispatch = useDispatch();
  //useReduxUserSync();
  //useAutoLogout();
  const navigate = useNavigate();
  
  const userInStore = useSelector((state) => state.auth.user); // Get user from Redux

  setTokenUtils({
    tokenGetter: () => {
      const state = store.getState();
      const token = state.auth?.user?.tokens?.access_token;
      //const roles = state.auth.roles;
      return token ? `Bearer ${token}` : null;
    },
    impersonatedUserGetter: () => {
      const state = store.getState();
      const user = state.auth.impersonatedUser;
      
      return user;
    }
  });

  useEffect(() => {
    if(!userInStore){
      return;
    }

    Promise.all([
      dispatch(fetchTasksFromAPI()),
      dispatch(fetchPatrolsFromAPI()),
      // dispatch(fetchConstants())
    ]).then(() => {
      dispatch({ type: 'WS_CONNECT' }); // Start WebSocket only after REST calls finish
    });

    if(userInStore.roles.includes(PATROL) && userInStore.roles.length == 1) {
      navigate('/patrol-ui');
    }
   
  }, [dispatch, userInStore]);

  return (
    <>
      <CssBaseline />
      <AuthRedirectHandler />
      <CleanupUrl />
        <AppTheme {...props} themeComponents={xThemeComponents}>
          <CssBaseline enableColorScheme />
          <Box sx={{ display: "flex" }}>
            <SideMenu />
            <AppNavbar />
            <Box
              component="main"
              sx={(theme) => ({
                flexGrow: 1,
                backgroundColor: theme.vars
                  ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                  : alpha(theme.palette.background.default, 1),
                //overflow: "auto",
                m: 0,
                p: 0,
                display: "flex", // Enables Flexbox
                justifyContent: "center", // Centers horizontally
                alignItems: "center", // Centers vertically
                width: "100%",
              })}
            >
              <NotificationSnackbar />
              
              <Routes>
                <Route path="/" element={<PrivateRoute element={<HomePage />} allowedRoles={[CONTROL_TOWER, CALL_HANDLER]} />} />
                <Route path="/login" element={<MockLoginPage />} />
                <Route path="/patrols" element={<PrivateRoute element={<PatrolsPage />} allowedRoles={[CONTROL_TOWER, CALL_HANDLER]} />} />
                <Route path="/tasks" element={<PrivateRoute element={<TasksPage />} allowedRoles={[CONTROL_TOWER, CALL_HANDLER]} />} />
                <Route path="/tasks/patrolId/:patrolId" element={<PrivateRoute element={<TasksPage />} allowedRoles={[CONTROL_TOWER, CALL_HANDLER]} />} />
                <Route path="/completed-tasks" element={<PrivateRoute element={<CompletedTasksPage />} allowedRoles={[CONTROL_TOWER, CALL_HANDLER]} />} />
                <Route path="/patrol-ui" element={<PrivateRoute element={<PatrolUIPage />} allowedRoles={[CONTROL_TOWER,PATROL, CALL_HANDLER]} />} />
                <Route path="/patrol-ui/patrolId/:patrolId" element={<PrivateRoute element={<PatrolUIPage />} allowedRoles={[CONTROL_TOWER,PATROL, CALL_HANDLER]} />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/update" element={<UpdateStorePage />} />
              </Routes>
            </Box>
          </Box>
        </AppTheme>
    </>
  );
};

const rootElement = document.getElementById("root");
const root = createRoot(rootElement);
root.render(
  <ErrorBoundary fallback={<ErrorPage />}>
    <AuthProvider {...cognitoAuthConfig}>
      <Provider store={store}>
        <AuthGate>
          <AppInitializer>
            <Router>
              <App />
            </Router>
          </AppInitializer>
        </AuthGate>
      </Provider>
    </AuthProvider>
  </ErrorBoundary>
);
